import { AlertCircle, Info, Plus, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";

import { DepositAccountDetailDrawer } from "../components/DepositAccountDetailDrawer";
import { DepositAccountsList } from "../components/DepositAccountsList";
import { OrgSelector } from "../components/OrgSelector";
import { RegisterDepositAccountDialog } from "../components/RegisterDepositAccountDialog";
import { useDepositAccounts } from "../hooks/useDepositAccounts";
import { useOrganizationsCatalog } from "../hooks/useOrganizationsCatalog";
import type { DepositAccountRow, DepositAccountsCorridor } from "../types/depositAccounts.types";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface DepositAccountsPageProps {
  corridor: DepositAccountsCorridor;
  /** Sidebar label of this corridor's menu entry — reused as the breadcrumb leaf. */
  breadcrumbLabel: string;
  /** Absolute route of the page (breadcrumb link target). */
  route: string;
}

/**
 * INTERNAL staff config of the deposit receiving accounts of one corridor (Mexico SPEI CLABEs / Argentina
 * CBU-CVU) — the account ↔ organization mapping that attributes push-detected deposits. Register + list only:
 * the backend exposes no edit/delete/deactivate/mint. Access is decided by the backend RBAC (`payments:read`
 * to list, `payments:write` to register) — 401/403 latches into the no-access view; legacy scopes are not
 * consulted.
 */
export function DepositAccountsPage({ corridor, breadcrumbLabel, route }: DepositAccountsPageProps) {
  const { t } = useTranslations(corridor.i18nNamespace);
  const { t: tGlobal } = useTranslations("global");

  const breadcrumb = useMemo(
    () => [{ label: tGlobal("sidebar_section_gestion") }, { label: breadcrumbLabel, to: route }],
    [tGlobal, breadcrumbLabel, route],
  );
  useAppShellBreadcrumb(breadcrumb);

  // Selected org lives in the URL so the view is shareable and refresh-safe.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawOrgParam = searchParams.get("organization_id");
  const selectedOrgId = rawOrgParam && UUID_PATTERN.test(rawOrgParam) ? rawOrgParam : null;

  const orgsCatalog = useOrganizationsCatalog();
  const { accounts, isLoading, hasError, hasAccess, refresh } = useDepositAccounts(corridor, selectedOrgId);

  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<DepositAccountRow | null>(null);

  const selectedOrg = selectedOrgId
    ? (orgsCatalog.organizations.find((org) => org.id === selectedOrgId) ?? null)
    : null;

  const handleOrgChange = (organizationId: string | null) => {
    setSearchParams(organizationId ? { organization_id: organizationId } : {}, { replace: true });
  };

  if (!hasAccess || orgsCatalog.accessDenied) {
    return <NoAccessView />;
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-2">
      {/* Title + register (register requires payments:write — the backend answers on submit) */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t("page_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("page_subtitle")}</p>
        </div>
        <ColocaButton
          variant="primary"
          icon={Plus}
          disabled={!selectedOrg}
          onClick={() => setIsRegisterOpen(true)}
        >
          {t("button_register")}
        </ColocaButton>
      </div>

      {/* Org selector */}
      <div className="rounded-xl border border-border bg-card p-4">
        <OrgSelector
          i18nNamespace={corridor.i18nNamespace}
          organizations={orgsCatalog.organizations}
          isLoading={orgsCatalog.isLoading}
          value={selectedOrgId}
          onValueChange={handleOrgChange}
        />
        {orgsCatalog.isTruncated && (
          <p className="mt-2 text-xs text-muted-foreground">{t("org_truncated")}</p>
        )}
        {orgsCatalog.hasError && (
          <div className="mt-3">
            <ColocaAlert
              variant="destructive"
              icon={AlertCircle}
              text={
                <span className="flex flex-wrap items-center gap-3">
                  {t("org_error")}
                  <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={orgsCatalog.refresh}>
                    {t("error_retry")}
                  </ColocaButton>
                </span>
              }
            />
          </div>
        )}
      </div>

      {/* Why this matters — a missing mapping means deposits are never credited */}
      <ColocaAlert variant="secondary" icon={Info} text={t("why_banner")} />

      {/* Accounts of the selected org */}
      {!selectedOrgId ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("no_org_prompt")}</p>
      ) : hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("error_load")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={() => void refresh()}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      ) : (
        <DepositAccountsList
          corridor={corridor}
          accounts={accounts}
          isLoading={isLoading}
          onSelect={setSelectedAccount}
          onRegister={() => setIsRegisterOpen(true)}
        />
      )}

      {selectedOrg && (
        <RegisterDepositAccountDialog
          corridor={corridor}
          open={isRegisterOpen}
          onClose={() => setIsRegisterOpen(false)}
          organization={selectedOrg}
          existingAccounts={accounts}
          onRegistered={() => {
            void refresh();
          }}
        />
      )}

      {selectedAccount && (
        <DepositAccountDetailDrawer
          corridor={corridor}
          account={selectedAccount}
          open={selectedAccount !== null}
          onClose={() => setSelectedAccount(null)}
        />
      )}
    </div>
  );
}
